import React from 'react';
import moment from 'moment';
import withStyles from '@material-ui/core/styles/withStyles';
import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles } from '@material-ui/core/styles';
import WarningOutlineIcon from '@material-ui/icons/ReportProblemOutlined';
import { adjustColorBrightness } from '../calendar/colors';
import { getZ3PConfigValue } from '../helper';

const useStyles = makeStyles(() => ({
  bufferTime: {
    borderBottomLeftRadius: '2px',
    borderBottomRightRadius: '2px',
  },
  driveTime: {
    position: 'relative',
    bottom: 0,
    borderTopLeftRadius: '2px',
    borderTopRightRadius: '2px',
  },
  eventContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  eventTextWrapper: {
    flexGrow: 1,
    padding: '1px 1px 0',
    position: 'relative',
    overflow: 'hidden',
    minHeight: '20px',
    display: 'flex',
    justifyContent: 'space-between',
  },
  eventText: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },
  title: {
    display: 'inline-block',
  },
  warningIcon: {
    verticalAlign: 'middle',
    fontSize: '20px',
    color: '#D64747',
  },
}));

const CalendarAppointment = ({
  eventInfo,
  appointment,
  timeZone,
  timeIsUnavailable,
  conflictReason,
}) => {
  const classes = useStyles();

  if (!appointment) {
    return null;
  }

  const formatTime = (time) => {
    return moment(time)
      .tz(timeZone)
      .format('h:mm');
  };

  const {
    total_labor_hours,
    start_time,
    end_time,
    buffer_time_minutes,
    drive_time_minutes,
    location_type,
  } = appointment;

  const isHomeService = location_type === 'home';
  const totalLaborHours = Math.round(total_labor_hours * 10) / 10;
  const startTime = formatTime(start_time);
  const endTime = formatTime(end_time);
  const bufferTimeMinutes = buffer_time_minutes;
  const driveTimeMinutes = drive_time_minutes;
  const title = `${startTime} - ${endTime}`;
  // Value retrieved from the inspector
  // Because the calendar has a fixed height, we can use this to
  // calculate the height of the buffer time and drive time
  const dispatchCalendarCellHeightInPx = 55;
  const calculateBufferDriveHeight = (minutes) =>
    (dispatchCalendarCellHeightInPx * minutes) / 60;
  const bufferTimeHours = Math.round((bufferTimeMinutes / 60) * 10) / 10;
  const driveTimeHours = Math.round((driveTimeMinutes / 60) * 10) / 10;
  const driveBufferColor = adjustColorBrightness(
    eventInfo.backgroundColor,
    -50,
  );
  const driveTimeHeightPx = calculateBufferDriveHeight(driveTimeMinutes);
  const bufferTimeHeightPx = calculateBufferDriveHeight(bufferTimeMinutes);

  const tooltipContent = (
    <>
      {isHomeService && (
        <span
          style={{ display: eventInfo.isDraggable ? 'none' : 'inline' }}
        >{`Drive time: ${driveTimeHours} hr${
          driveTimeHours === 1 ? '' : 's'
        }`}</span>
      )}
      <span>{`Appointment length: ${totalLaborHours} hr${
        totalLaborHours === 1 ? '' : 's'
      }`}</span>
      <span style={{ display: 'inline' }}>{`Buffer time: ${bufferTimeHours} hr${
        bufferTimeHours === 1 ? '' : 's'
      }`}</span>
    </>
  );

  const conflictTooltipContent = <span>{conflictReason}</span>;

  const HtmlTooltip = withStyles((theme) => ({
    tooltip: {
      maxWidth: 220,
      fontSize: theme.typography.pxToRem(12),
      display: eventInfo.isDragging ? 'none' : 'flex',
      flexDirection: 'column',
    },
  }))(Tooltip);

  return (
    <HtmlTooltip
      title={conflictReason ? conflictTooltipContent : tooltipContent}
    >
      <div className={classes.eventContainer}>
        {isHomeService && (
          <div
            className={classes.driveTime}
            style={{
              display: eventInfo.isDraggable ? 'none' : 'block',
              backgroundColor: driveBufferColor,
              height: `${driveTimeHeightPx}px`,
            }}
          />
        )}
        <div className={classes.eventTextWrapper}>
          <div className={classes.eventText}>
            <span className={classes.title}>{eventInfo.event.title}</span>
            {totalLaborHours > 0.5 && (
              <span className={classes.title}>{title}</span>
            )}
          </div>
          {timeIsUnavailable && eventInfo.isDraggable && (
            <div>
              <WarningOutlineIcon className={classes.warningIcon} />
            </div>
          )}
        </div>
        <div
          className={classes.bufferTime}
          style={{
            display: eventInfo.isDraggable ? 'none' : 'block',
            backgroundColor: driveBufferColor,
            height: `${bufferTimeHeightPx}px`,
          }}
        />
      </div>
    </HtmlTooltip>
  );
};

export default CalendarAppointment;
